import { PrintableDocument } from "@redotech/redo-fulfillment-group-common/print-job";
import { PageFormat } from "@redotech/redo-fulfillment-outbound-labels-common/outbound-label-enums";
import { PageSize } from "@redotech/redo-model/packing-slips/packing-slip-builder";

export const DERMAZEN_TEAM_ID = "69e6a22324b42422b56ac9ee";

export interface PackingSlipAutoPrintTemplate {
  pageLayout: { size: PageSize };
  combineHalfPageSlips?: boolean | null;
}

export interface PackingSlipOnLabelPrintedDecisionInput {
  document: PrintableDocument;
  labelPageFormat: PageFormat;
  packingSlipPageFormat: PageFormat;
  printSlipOnLabelPrinted: boolean | null | undefined;
  teamId: string;
  packingSlips: readonly PackingSlipAutoPrintTemplate[];
}

function getPackingSlipOnLabelPrintedBaseCondition({
  document,
  labelPageFormat,
  printSlipOnLabelPrinted,
}: PackingSlipOnLabelPrintedDecisionInput) {
  if (document !== PrintableDocument.LABEL) {
    return false;
  }

  if (labelPageFormat !== PageFormat.Label) {
    return false;
  }

  return !!printSlipOnLabelPrinted;
}

export function getFullSizePackingSlipOldLogic(
  input: PackingSlipOnLabelPrintedDecisionInput,
) {
  if (!getPackingSlipOnLabelPrintedBaseCondition(input)) {
    return false;
  }

  if (input.packingSlips.length === 0) {
    return false;
  }

  const everyPackingSlipIsFullSizeLayout = input.packingSlips.every(
    (t) => t.pageLayout.size === PageSize.FULL_SIZE,
  );

  return everyPackingSlipIsFullSizeLayout;
}

export function getDermazenPackingSlipNewLogic(
  input: PackingSlipOnLabelPrintedDecisionInput,
) {
  if (!getMergedHalfPagePackingSlipNewLogic(input)) {
    return false;
  }

  return input.teamId === DERMAZEN_TEAM_ID;
}

export function getMergedHalfPagePackingSlipNewLogic(
  input: PackingSlipOnLabelPrintedDecisionInput,
) {
  if (!getPackingSlipOnLabelPrintedBaseCondition(input)) {
    return false;
  }

  if (input.packingSlipPageFormat !== PageFormat.Letter) {
    return false;
  }

  if (input.packingSlips.length === 0) {
    return false;
  }

  return input.packingSlips.every(
    (t) => t.pageLayout.size === PageSize.HALF_SIZE && !!t.combineHalfPageSlips,
  );
}

export function getShouldPrintPackingSlipOnLabelPrinted(
  input: PackingSlipOnLabelPrintedDecisionInput,
) {
  return (
    getFullSizePackingSlipOldLogic(input) ||
    getDermazenPackingSlipNewLogic(input)
  );
}

export function shouldPrintPackingSlipOnLabelPrintedWithFullSizeLogic(
  input: PackingSlipOnLabelPrintedDecisionInput,
) {
  return getFullSizePackingSlipOldLogic(input);
}

export function shouldPrintPackingSlipOnLabelPrintedWithLetterFormat(
  input: PackingSlipOnLabelPrintedDecisionInput,
) {
  return (
    getPackingSlipOnLabelPrintedBaseCondition(input) &&
    input.packingSlipPageFormat === PageFormat.Letter
  );
}
